import { valuationAttribution, VALUATION_EVENTS } from './valuation';

const canTrack = () =>
  typeof window !== 'undefined' &&
  window.uroboroxAnalyticsConsent === true &&
  typeof window.gtag === 'function';

export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url);
  }

  trackValuation(params) {
    console.log('Analytics valuation:', params);
  }

  trackSignUp(params) {
    console.log('Analytics sign_up:', params);
  }

  trackGenerateLead() {
    console.log('Analytics generate_lead');
  }

  trackListingPublished(params) {
    console.log('Analytics listing_published:', params);
  }
}

/**
 * Serializa el array de intereses del alta a un valor estable para GA4.
 *
 * Se ordena ALFABÉTICAMENTE a propósito: sin orden fijo, el mismo combo de
 * opciones generaría variantes distintas ("comprar|invertir" vs
 * "invertir|comprar") y multiplicaría las filas en los informes.
 *
 * Devuelve null si no hay nada que mandar, para poder omitir el parámetro.
 */
const serializeInterests = interests => {
  if (!Array.isArray(interests) || interests.length === 0) {
    return null;
  }
  // Copia antes de ordenar: sort() muta el array original.
  return [...interests].sort().join('|');
};

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && canTrack()) {
      const revision = window.uroboroxAnalyticsConsentRevision;
      window.setTimeout(() => {
        if (!canTrack() || revision !== window.uroboroxAnalyticsConsentRevision) return;
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }

  /**
   * Evento `sign_up`, tras un alta correcta.
   *
   * NADA DE DATOS PERSONALES: solo el tipo de perfil y las opciones de interés,
   * que son slugs de un enum cerrado. Ni email, ni nombre, ni teléfono.
   *
   * @param {Object} params
   * @param {string} [params.userType] - publicData.userType. Va como user
   *   property `tipo_perfil`, no como parámetro del evento.
   * @param {Array<string>} [params.interests] - publicData.interes.
   */
  trackSignUp({ userType, interests, origin, intent } = {}) {
    // Check current consent even if GA was loaded earlier in this visit.
    if (!canTrack()) {
      return;
    }

    // La user property se fija ANTES del evento para que sign_up ya viaje
    // asociado al perfil. gtag('set') no envía nada por sí solo: se adjunta a
    // los hits posteriores.
    if (userType) {
      window.gtag('set', 'user_properties', { tipo_perfil: userType });
    }

    const interestsValue = serializeInterests(interests);
    const interesesMaybe = interestsValue ? { intereses: interestsValue } : {};

    window.gtag('event', 'sign_up', {
      // Hoy solo hay alta con email; no hay proveedor externo instrumentado.
      method: 'email',
      ...valuationAttribution({ origin, intent }),
      ...interesesMaybe,
    });
  }

  /** Send only the closed listing-type enum after a confirmed publication. */
  trackListingPublished({ listingType, origin, intent } = {}) {
    if (!canTrack() || !['free-listing', 'mandato-compra'].includes(listingType)) {
      return;
    }

    window.gtag('event', 'listing_published', {
      listing_type: listingType,
      ...valuationAttribution({ origin, intent }),
    });
  }

  /** Closed enums only: never forward answers or identifiers to GA4. */
  trackValuation({ event, intent } = {}) {
    if (!canTrack() || !VALUATION_EVENTS.includes(event)) return;
    const params = valuationAttribution({ origin: 'valorador', intent });
    if (!params.intent) return;
    window.gtag('event', event, params);
  }

  /**
   * Evento `generate_lead`, tras un envío correcto del formulario de contacto.
   *
   * Sin parámetros propios: la procedencia (utm_source / utm_medium /
   * utm_campaign) ya la recoge GA4 en sus dimensiones nativas de origen, medio
   * y campaña, así que reenviarla sería duplicarla.
   *
   * NADA DE DATOS PERSONALES: ni email, ni teléfono, ni el texto de la consulta.
   */
  trackGenerateLead() {
    if (!canTrack()) {
      return;
    }

    window.gtag('event', 'generate_lead');
  }
}
